import type { Project } from "../../types/project";
import type { StoryMapSummary } from "../../types/storyMap";
import { coverStaticMapUrl } from "../storyMap/storyLayers";
import { type FolderIndex, type FolderKey, descendantIds, normalizeFolderId } from "./folderTree";
import { type Bbox, projectBbox } from "./projectBbox";

/**
 * Story maps filed into the folder tree, for the listing's story-map browse
 * mode. A project story sits where its project does; a folder story in its
 * folder; an organisation-wide story at the top level. Built client-side from
 * the tenant-wide story list and the project list, like the folder index.
 */
export interface StoryIndex {
  byId: ReadonlyMap<string, StoryMapSummary>;
  /** Stories filed directly in each folder (null = the top level). */
  inFolder: ReadonlyMap<FolderKey, StoryMapSummary[]>;
  /** Project stories by project id. */
  byProject: ReadonlyMap<number, StoryMapSummary[]>;
  /** Folder-level stories by folder id. */
  byFolder: ReadonlyMap<number, StoryMapSummary[]>;
  /** Organisation-wide stories. */
  root: StoryMapSummary[];
  /** Stories of archived projects (the listing's Archive). */
  archived: StoryMapSummary[];
}

const EMPTY: StoryMapSummary[] = [];

/** Newest edit first, so a group's first story is the one worth a thumbnail. */
export const byUpdatedDesc = (a: StoryMapSummary, b: StoryMapSummary) =>
  Date.parse(b.updated_at || "") - Date.parse(a.updated_at || "") || a.title.localeCompare(b.title);

export function buildStoryIndex(
  stories: StoryMapSummary[],
  projects: Project[],
  index: FolderIndex,
  /** Archived projects: their stories go to the Archive, not a folder. */
  archivedProjects: Project[] = []
): StoryIndex {
  const projectsById = new Map<number, Project>();
  projects.forEach((p) => projectsById.set(Number(p.id), p));
  const archivedIds = new Set(archivedProjects.map((p) => Number(p.id)));
  const byId = new Map<string, StoryMapSummary>();
  const inFolder = new Map<FolderKey, StoryMapSummary[]>();
  const byProject = new Map<number, StoryMapSummary[]>();
  const byFolder = new Map<number, StoryMapSummary[]>();
  const root: StoryMapSummary[] = [];
  const archived: StoryMapSummary[] = [];
  const file = (key: FolderKey, story: StoryMapSummary) => {
    const list = inFolder.get(key);
    if (list) list.push(story);
    else inFolder.set(key, [story]);
  };
  [...stories].sort(byUpdatedDesc).forEach((story) => {
    if (story.project_id !== null && story.project_id !== undefined) {
      if (archivedIds.has(Number(story.project_id))) {
        byId.set(story.id, story);
        archived.push(story);
        const list = byProject.get(Number(story.project_id));
        if (list) list.push(story);
        else byProject.set(Number(story.project_id), [story]);
        return;
      }
      // A story whose project is in neither list (still loading) has nowhere
      // to sit, so it is left out rather than filed at the root.
      const project = projectsById.get(Number(story.project_id));
      if (!project) return;
      byId.set(story.id, story);
      file(normalizeFolderId(index, project.folder), story);
      const list = byProject.get(Number(story.project_id));
      if (list) list.push(story);
      else byProject.set(Number(story.project_id), [story]);
      return;
    }
    byId.set(story.id, story);
    if (story.folder_id !== null && story.folder_id !== undefined) {
      const key = normalizeFolderId(index, story.folder_id);
      file(key, story);
      if (key !== null) {
        const list = byFolder.get(key);
        if (list) list.push(story);
        else byFolder.set(key, [story]);
      }
      return;
    }
    file(null, story);
    root.push(story);
  });
  return { byId, inFolder, byProject, byFolder, root, archived };
}

export function storiesInFolder(si: StoryIndex, key: FolderKey): StoryMapSummary[] {
  return si.inFolder.get(key) ?? EMPTY;
}

/** Stories anywhere beneath a folder, the folder itself included. */
export function subtreeStoryCount(si: StoryIndex, index: FolderIndex, folderId: number): number {
  const ids = descendantIds(index, folderId);
  ids.add(folderId);
  let count = 0;
  ids.forEach((id) => {
    count += si.inFolder.get(id)?.length ?? 0;
  });
  return count;
}

/** What a story draws from, as the Scope column reads it. */
export function storyScopeLabel(story: StoryMapSummary, index: FolderIndex): string {
  if (story.project_id !== null && story.project_id !== undefined) return story.project_name ?? "Project";
  if (story.folder_id !== null && story.folder_id !== undefined) {
    const name = index.byId.get(story.folder_id)?.name;
    return name ? `${name} folder` : "Folder";
  }
  return "All projects";
}

/**
 * A story's cover image: the first image in it, else a static map of its
 * first scene. Null for a story with no scenes and no images.
 */
export function storyThumbnailUrl(story: StoryMapSummary, width = 144, height = 90): string | null {
  if (story.cover?.image_url) return story.cover.image_url;
  if (story.cover?.camera?.center) return coverStaticMapUrl(story.cover.camera, width, height);
  return null;
}

export type StoryGroupKind = "project" | "folder";

/** The stories a map marker stands for: one project's, or one folder's. */
export interface StoryMarkerGroup {
  key: string;
  kind: StoryGroupKind;
  id: number;
  label: string;
  bbox: Bbox;
  /** Newest first; the first is the thumbnail shown. */
  stories: StoryMapSummary[];
}

export const projectGroupKey = (projectId: number | string) => `project:${projectId}`;
export const folderGroupKey = (folderId: number) => `folder:${folderId}`;

/**
 * Group the listed stories by where they sit on the map. Project stories
 * gather on their project; folder stories on their folder's extent. Stories
 * with nowhere to sit (an unlocated project, an organisation-wide story) come
 * back separately.
 */
export function groupStoriesForMap(
  stories: StoryMapSummary[],
  projectsById: ReadonlyMap<number, Project>,
  index: FolderIndex,
  folderExtentFor: (folderId: number) => Bbox | null,
  isDrawn: (story: StoryMapSummary) => boolean
): { groups: StoryMarkerGroup[]; unplaced: StoryMapSummary[] } {
  const groups = new Map<string, StoryMarkerGroup>();
  const unplaced: StoryMapSummary[] = [];
  const add = (key: string, make: () => Omit<StoryMarkerGroup, "key" | "stories"> | null, story: StoryMapSummary) => {
    const existing = groups.get(key);
    if (existing) {
      existing.stories.push(story);
      return;
    }
    const base = make();
    if (!base) {
      unplaced.push(story);
      return;
    }
    groups.set(key, { key, ...base, stories: [story] });
  };
  [...stories].sort(byUpdatedDesc).forEach((story) => {
    if (!isDrawn(story)) return;
    if (story.project_id !== null && story.project_id !== undefined) {
      const project = projectsById.get(Number(story.project_id));
      add(
        projectGroupKey(story.project_id),
        () => {
          const bbox = project ? projectBbox(project) : null;
          return project && bbox ? { kind: "project", id: Number(project.id), label: project.name, bbox } : null;
        },
        story
      );
    } else if (story.folder_id !== null && story.folder_id !== undefined) {
      const folder = index.byId.get(story.folder_id);
      add(
        folderGroupKey(story.folder_id),
        () => {
          const bbox = folder ? folderExtentFor(folder.id) : null;
          return folder && bbox ? { kind: "folder", id: folder.id, label: folder.name, bbox } : null;
        },
        story
      );
    } else {
      unplaced.push(story);
    }
  });
  return { groups: Array.from(groups.values()), unplaced };
}

/** The group a story belongs to, for linking a hovered row to its marker. */
export function storyGroupKey(story: StoryMapSummary): string | null {
  if (story.project_id !== null && story.project_id !== undefined) return projectGroupKey(story.project_id);
  if (story.folder_id !== null && story.folder_id !== undefined) return folderGroupKey(story.folder_id);
  return null;
}
